import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, ButtonGroup, LinearProgress, FormHelperText, FormControl, Select, MenuItem, InputLabel, FormControlLabel, Checkbox, Chip, TextField } from "@material-ui/core";
import { getBookingRules, AudienceGroup, BookingRules, BookingType, InviteableUser, CreateBookingParams, postBookings, Booking, patchFundsRecipients, BookingFundsRecipients, PublicTag, getTags, CompanyClub } from "api/bookings";
import moment from "moment";
import { Membership } from "api/memberships";
import { Club } from "api/clubs";
import { GridTextField, AudienceSelect, NumberPicker, DateSelect } from "components";
import CurrencyTextField from "@unicef/material-ui-currency-textfield";
import { DatePicker } from "@material-ui/pickers";
import { postComment } from "api/reactions";
import config from "config";
import { UserProfile } from "api/user";
import getClubs from "api/root/clubs";
import uI18n from "utils/i18n";
import { Autocomplete } from "@material-ui/lab";
import { isAllowRequestTypes, isUserBelongsToRoot } from "utils/helpers";
import DurationPicker from "./DurationPicker";
import StartDatePicker from "./StartDatePicker";
import ParticipantsPicker from "./ParticipantsPicker";
import CourtPicker from "./CourtPicker";
import i18n from "../i18n";

// prop types
interface CreateBookingDialogProps {
    club: Club | null,
    defaultCourt?: string,
    defaultStartDate?: string,
    show: boolean,
    template?: Booking,
    userRole?: Membership["userRole"],
    userProfile: UserProfile | null,
    onClose: () => void,
    onFetchBookings: (clubId: string, date: string) => void,
    getCompanyBookings: (clubId: string) => void,
}

// currency formatter
const formatter = Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// create booking dialog
const CreateBookingDialog = ({ club, defaultCourt, defaultStartDate, show, template, userProfile, userRole, onClose, onFetchBookings, getCompanyBookings }: CreateBookingDialogProps) => {
    // ui state
    const [addAudienceGroupException, setAddAudienceGroupException] = useState(false);
    const [allDay, setAllDay] = useState(false);
    const [audienceGroupSpecification, setAudienceGroupSpecification] = useState<AudienceGroup>();
    const [autoAcceptParticipationForGroup, setAutoAcceptParticipationForGroup] = useState<AudienceGroup>();
    const [attachedMedia, setAttachedMedia] = useState<File[]>([]);
    const [attachedMediaComment, setAttachedMediaComment] = useState<string>("");
    const [bookingRules, setBookingRules] = useState<BookingRules | null>(null);
    const [blockingBlueprint, setBlockingBlueprint] = useState<CreateBookingParams["blueprint"]>("court-blocking");
    const [coaches, setCoaches] = useState<string[]>([]);
    const [coronaTracing, setCoronaTracing] = useState<boolean>(false);
    const [court, setCourt] = useState("");
    const [customMaxNumParticipants, setCustomMaxNumParticipants] = useState(0);
    const [customMinNumParticipants, setCustomMinNumParticipants] = useState(0);
    const [description, setDescription] = useState("");
    const [privateDescription, setPrivateDescription] = useState("");
    const [detailsDescription, setDetailsDescription] = useState("");
    const [duration, setDuration] = useState(60);
    const [error, setError] = useState<string | null>(null);
    const [eventType, setEventType] = useState<"standard" | "competition" | "video-conference" | "video-stream" | "commercial">("standard");
    const [finalizationDeadline, setFinalizationDeadline] = useState<string>();
    const [fundsRecipientChoices, setFundsRecipientChoices] = useState<BookingFundsRecipients["recipientChoices"]>([]);
    const [fundsRecipientId, setFundsRecipientId] = useState("");
    const [hideCreator, setHideCreator] = useState(false);
    const [creatorMediaOnly, setCreatorMediaOnly] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [negatedAudienceGroupSpecification, setNegatedAudienceGroupSpecification] = useState<AudienceGroup>();
    const [participants, setParticipants] = useState<InviteableUser[]>([]);
    const [tags, setTags] = useState<PublicTag[]>([]);
    const [publicTag, setPublicTags] = useState<PublicTag[]>([]);
    const [participationRequestType, setParticipationRequestType] = useState<CreateBookingParams["participationRequestType"]>("requestable-by-followers");
    const [participantSpecificCheckIn, setParticipantSpecificCheckIn] = useState(false);
    const [paywhatyouwant, setPaywhatyouwant] = useState(false);
    const [repeatType, setRepeatType] = useState<CreateBookingParams["repeatType"]>("never");
    const [repeatEndDate, setRepeatEndDate] = useState(moment().startOf("day").format("YYYY-MM-DD"));
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [sportForm, setSportForm] = useState("");
    const [standardParticipationPrice, setStandardParticipationPrice] = useState("0");
    const [startDate, setStartDate] = useState(moment().add(60, "minutes").toISOString());
    const [type, setType] = useState(BookingType.Standard);
    const [typeOverride, setTypeOverride] = useState(BookingType.Standard); // if available use this type (tennis courts hate meetups)
    const [companyExclusive, setCompanyExclusive] = useState(false);
    const typeRules = bookingRules?.availableBookingTypes.find((t) => t.bookingType === type);
    const isAllDay = allDay && (type === BookingType.Blocking || type === BookingType.Training);
    const insertMediaRef = useRef<HTMLInputElement>(null);
    const [companyClubs, setCompanyClubs] = useState<CompanyClub[]>([]);
    const [exclusiveCompanyClubId, setExclusiveCompanyClubId] = useState("");
    const [exclusiveCompanyPriceCents, setExclusiveCompanyPriceCents] = useState("0");
    const [selectedCompanyExclusiveClub, setSelectedCompanyExclusiveClub] = useState<CompanyClub>();

    // training rules (to always show event type for owners & admins)
    const trainingRules: BookingRules["availableBookingTypes"][number] = {
        allowsFinalizationDeadline: true,
        allowsParticipantSpecificCheckIn: true,
        allowsParticipants: true,
        allowsStandardParticipationPrice: true,
        availableRepeatTypes: ["never", "daily", "weekly", "biweekly", "monthly"],
        bookingType: BookingType.Training,
        repeated: true,
        requiresDescription: true,
        requiresParticipants: false,
    };

    // get booking rules on mount, on change
    const fetchBookingRules = async () => {
        const rules = await getBookingRules({
            bookingType: type,
            courtId: court,
            durationMins: (duration > 0 ? duration : undefined),
            startDate,
        });
        if (["owner", "admin"].includes(userRole!) && !rules.availableBookingTypes.find((t) => t.bookingType === BookingType.Training)) {
            rules.availableBookingTypes.push(trainingRules); // always show event type for owners & admins
        }
        setBookingRules(rules);
        if (!rules.availableBookingTypes.find((t) => t.bookingType === typeOverride) && rules.availableBookingTypes.length > 0) {
            setType(rules.availableBookingTypes[0].bookingType); // set to an allowed type
        } else {
            setType(typeOverride);
        }
    };

    // fetch dropdown exclusive company clubs
    const companyExclusiveDropdownClubs = async () => {
        const newResponse: CompanyClub[] = [];
        if (isRootUser()) {
            newResponse.push({
                id: "all",
                clubName: i18n.allOrganizations,
            });
            const companies = await getClubs({ capabilities: ["company"], limit: 100 });
            companies.data?.forEach((c) => {
                newResponse.push({
                    id: c.id,
                    clubName: c.clubName,
                });
            });
        }
        if (!isRootUser() && ["owner", "admin", "trainer"].includes(userRole!)) {
            const memberCompanyOrPublicGroundClubs = userProfile?.memberships?.filter((m) => m?.club?.capabilities.includes("company"));
            const clubData = memberCompanyOrPublicGroundClubs?.filter((m) => m.userRole === "owner" || m.userRole === "trainer" || m.userRole === "admin");
            clubData?.forEach((c) => {
                newResponse.push({
                    id: c.club.id,
                    clubName: c.club.clubName,
                });
            });
        }
        if (!selectedCompanyExclusiveClub && newResponse?.length > 0) {
            if (newResponse?.length > 0 && newResponse[0].id !== "all") {
                setSelectedCompanyExclusiveClub({ id: newResponse[0].id, clubName: newResponse[0].clubName });
            }
        }
        setCompanyClubs(newResponse);
    };

    const courtObject = club?.courts.find((c) => c.id === court);
    useEffect(() => { if (userRole && court) fetchBookingRules(); }, [userRole, court, startDate, type, typeOverride, duration]); // eslint-disable-line
    useEffect(() => { if (club && club.courts.length > 0) setCourt(club.courts[0].id); }, [club]);
    useEffect(() => { if (club && eventType === "commercial" && !club.courts.find((c) => c.id === court)?.isCommercialOnlyCourt) setEventType("standard"); }, [club, court, eventType]);
    useEffect(() => { if (defaultCourt) setCourt(defaultCourt); }, [defaultCourt]);
    useEffect(() => { if (defaultStartDate) { setStartDate(defaultStartDate); setExclusiveCompanyClubId(""); } }, [defaultStartDate]);
    useEffect(() => { if (courtObject && courtObject.sportForms.length > 0) setSportForm(courtObject.sportForms[0]); }, [courtObject]);
    useEffect(() => {
        if (type === BookingType.Training && canCreateCompanyExclusive()) {
            companyExclusiveDropdownClubs();
        }
        updateRequestType();
    }, [companyExclusive, type, club]);

    const updateRequestType = () => {
        if (type === BookingType.Training && club && !isAllowRequestTypes(club)) {
            setParticipationRequestType("auto-accept-for-group");
            setAutoAcceptParticipationForGroup({ ...autoAcceptParticipationForGroup, clubId: club?.id });
        } else if ((type === BookingType.Training && club && isAllowRequestTypes(club)) || type !== BookingType.Training) {
            setParticipationRequestType("requestable-by-followers");
        }
    };

    // apply booking template
    useEffect(() => {
        if (template) {
            const inviteableUsers: InviteableUser[] = template.participants
                .filter((p) => p.userId !== userProfile?.id)
                .filter((p) => p.participantRole === "coach")
                .map((p) => ({
                    firstName: p.user.firstName,
                    followingYou: false,
                    id: p.userId,
                    isInviteableAsCoach: p.participantRole === "coach",
                    lastName: p.user.lastName,
                    profilePictureUrl: p.user.profilePictureUrl,
                }));
            if (template.autoAcceptParticipationForGroup) setAutoAcceptParticipationForGroup(template.autoAcceptParticipationForGroup);
            setCoaches(template.participants.filter((p) => p.participantRole === "coach").map((p) => p.userId));
            setCourt(template.court.id);
            setCustomMaxNumParticipants(template.customMaxNumParticipants || 0);
            setCustomMinNumParticipants(template.customMinNumParticipants || 0);
            setDescription(template.description || "");
            setPrivateDescription(template.privateDescription || "");
            setDetailsDescription(template.detailsDescription || "");
            setDuration(moment(template.endDate).diff(template.startDate, "minutes"));
            if (template.finalizationDeadline) setFinalizationDeadline(template.finalizationDeadline);
            if (template.fundsRecipientId) setFundsRecipientId(fundsRecipientId);
            if (template.hideCreator) setHideCreator(template.hideCreator);
            if (template.creatorMediaOnly) setCreatorMediaOnly(template.creatorMediaOnly);
            setParticipants(inviteableUsers);
            setParticipationRequestType(template.participationRequestType);
            setParticipantSpecificCheckIn(template.participantSpecificCheckIn);
            // if (template.repeatEndDate) setRepeatEndDate(template.repeatEndDate);
            // setRepeatType(template.repeatType);
            setShowAdvanced(true);
            setSportForm(template.sportForm);
            if (template.standardParticipationPriceCents) setStandardParticipationPrice((template.standardParticipationPriceCents / 100).toString());
            setStartDate(template.startDate);
            setType(template.bookingType);
            setTypeOverride(template.bookingType);
        }
    }, [template]);

    // fetch funds recipients
    const fetchFundsRecipients = async () => {
        const { recipientChoices } = await patchFundsRecipients({
            courtId: court,
            hideCreator,
            participants: participants.map(({ id }) => ({ participantRole: coaches.includes(id) ? "coach" : "standard", userId: id })),
        });
        setFundsRecipientChoices(recipientChoices);
        if (recipientChoices.length > 0 && !recipientChoices.map((c) => c.id).includes(fundsRecipientId)) setFundsRecipientId(recipientChoices[0].id);
    };
    useEffect(() => { if (Number(standardParticipationPrice) > 0) fetchFundsRecipients(); }, [court, standardParticipationPrice, participants, coaches, hideCreator]);

    // relevant errors (handled by pickers)
    const relevantErrors = ["bookingNeedsAtLeastOneInvitee", "courtIsBooked", "descriptionTooShort", "outOfOpeningHours", "bookingTooLong", "bookingDetailsDescriptionTooLong"];

    // create booking
    const createBooking = async () => {
        // convert parameters
        const { closeTimeMins, openTimeMins } = club?.courts.find((c) => c.id === court)!;
        const allDayStart = moment(startDate).startOf("day").set({ hour: Math.floor(openTimeMins / 60), minute: openTimeMins % 60 }).toISOString();
        const bookingTags: Booking["bookingTags"] = (type === BookingType.Training && eventType !== "standard") ? [eventType] : [];
        if (paywhatyouwant) bookingTags.push("pay-what-you-want");
        if (companyExclusive && type === BookingType.Training && canCreateCompanyExclusive()) {
            bookingTags.push("company-exclusive");
        }
        if (typeRules?.allowsParticipants && coronaTracing) bookingTags.push("corona");
        const booking: CreateBookingParams = {
            audienceGroupSpecification,
            autoAcceptParticipationForGroup: (showAdvanced ? autoAcceptParticipationForGroup : undefined),
            blueprint: (typeRules?.blueprints ? blockingBlueprint : undefined),
            bookingTags,
            tags: tags.map((t) => t.token),
            bookingType: type,
            clientLatitude: club?.latitude,
            clientLongitude: club?.longitude,
            courtIds: [court],
            customMaxNumParticipants: ((showAdvanced && typeRules?.allowsParticipants && customMaxNumParticipants > 0) ? customMaxNumParticipants : undefined),
            customMinNumParticipants: ((showAdvanced && typeRules?.allowsParticipants && customMinNumParticipants > 0) ? customMinNumParticipants : undefined),
            description: (typeRules?.requiresDescription ? description : undefined),
            privateDescription,
            detailsDescription,
            durationMins: (isAllDay ? closeTimeMins - openTimeMins : duration),
            expectedPriceCents: bookingRules?.priceCents,
            finalizationDeadline,
            fundsRecipientId: (type === BookingType.Training && (Number(standardParticipationPrice) > 0 || paywhatyouwant)) ? fundsRecipientId : undefined,
            hideCreator: (type === BookingType.Training || type === BookingType.Blocking) ? hideCreator : undefined,
            creatorMediaOnly: (type === BookingType.Training) ? creatorMediaOnly : undefined,
            negatedAudienceGroupSpecification: (addAudienceGroupException ? negatedAudienceGroupSpecification : undefined),
            participants: (typeRules?.allowsParticipants ? participants.map(({ id }) => ({ participantRole: coaches.includes(id) ? "coach" : "standard", userId: id })) : undefined),
            participationRequestType: (showAdvanced ? participationRequestType : undefined),
            participantSpecificCheckIn: ((showAdvanced && typeRules?.allowsParticipantSpecificCheckIn) ? participantSpecificCheckIn : undefined),
            sportForm: (type === BookingType.Training) ? sportForm : undefined,
            standardParticipationPriceCents: ((showAdvanced && typeRules?.allowsStandardParticipationPrice) ? Number(standardParticipationPrice) * 100 : undefined),
            startDate: (isAllDay ? allDayStart : startDate),
            repeatEndDate: (typeRules?.repeated ? repeatEndDate : undefined),
            repeatType: (typeRules?.repeated ? repeatType : undefined),
            exclusiveCompanyClubId: ((companyExclusive && type === BookingType.Training && canCreateCompanyExclusive() && exclusiveCompanyClubId !== "" && exclusiveCompanyClubId !== "all") ? exclusiveCompanyClubId : undefined),
            exclusiveCompanyPriceCents: ((companyExclusive && type === BookingType.Training && canCreateCompanyExclusive() && exclusiveCompanyClubId !== "" && exclusiveCompanyClubId !== "all") ? Number(exclusiveCompanyPriceCents) * 100 : undefined),
        };

        // api request
        try {
            setLoading(true);
            setError(null);
            if (typeRules?.requiresDescription && description.length < 4) throw { type: "descriptionTooShort" }; // eslint-disable-line
            const createdBookings = await postBookings(booking);

            // add media via comment to all bookings in the series
            if (attachedMedia.length > 0 && type === BookingType.Training) {
                for (let i = 0; i < attachedMedia.length; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    await Promise.all(createdBookings.map(async (createdBooking) => (
                        postComment({
                            content: i === 0 ? attachedMediaComment : "",
                            bookingId: createdBooking.id,
                            attachedMedia: attachedMedia[i],
                        })
                    )));
                }
            }

            // update & reset
            onFetchBookings(club!.id, startDate);
            getCompanyBookings(club!.id);
            onClose();
            setAllDay(false);
            setAttachedMedia([]);
            setAttachedMediaComment("");
            setCoronaTracing(false);
            setDescription("");
            setPrivateDescription("");
            setDetailsDescription("");
            setDuration(bookingRules!.minDurationMins);
            setParticipants([]);
            setTags([]);
            setCoaches([]);
            setShowAdvanced(false);
            setSportForm((courtObject && courtObject.sportForms.length > 0) ? courtObject.sportForms[0] : "");
            setType(BookingType.Standard);
            setTypeOverride(BookingType.Standard);
            setEventType("standard");
            setStandardParticipationPrice("0");
            setPaywhatyouwant(false);
            setHideCreator(false);
            setCreatorMediaOnly(false);
            setFinalizationDeadline(undefined);
        } catch (err: any) {
            if (relevantErrors.includes(err.type)) {
                setError(err.type); // known and handled by pickers
            } else {
                setError(`${i18n.anErrorOccurred}: ${err.description} (${err.type})`); // unknown
            }
        }
        setLoading(false);
    };

    // fetch all tags
    const fetchPublicTags = async () => { setPublicTags((await getTags()).data); };
    useEffect(() => { fetchPublicTags(); }, []);

    const isRootUser = () => isUserBelongsToRoot(userProfile?.userTypes);

    const canCreateCompanyExclusive = () => (isRootUser() || ["owner", "admin", "trainer"].includes(userRole!));

    const onSelectExclusiveClub = (v: CompanyClub | null) => {
        if (v) {
            setSelectedCompanyExclusiveClub({ id: v?.id, clubName: v?.clubName });
        } else {
            setSelectedCompanyExclusiveClub({ id: "", clubName: "" });
            setExclusiveCompanyClubId("");
        }
    };

    // render
    const detailsDescriptionLength = config.bookings.getStrippedLength(detailsDescription);
    return bookingRules && (
        <Dialog
            fullWidth
            maxWidth="md"
            open={show}
            onClose={onClose}
        >
            <DialogTitle>{i18n.newBooking}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>

                    {/* booking type */}
                    <Grid item xs={12}>
                        <ButtonGroup color="primary" fullWidth size="medium">
                            {bookingRules.availableBookingTypes.map(({ bookingType }) => (
                                <Button
                                    disableElevation={bookingType === type}
                                    key={bookingType}
                                    variant={(bookingType === type) ? "contained" : "outlined"}
                                    onClick={() => { setType(bookingType); setTypeOverride(bookingType); }}
                                >
                                    {i18n.bookingTypes[bookingType]}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </Grid>

                    {/* start date */}
                    <Grid item>
                        {isAllDay ? (
                            <DatePicker
                                disablePast
                                value={startDate}
                                variant="static"
                                onChange={(m) => setStartDate(m!.toISOString())}
                            />
                        ) : (
                            <StartDatePicker
                                bookingRules={bookingRules}
                                duration={duration}
                                startDate={startDate}
                                onChange={setStartDate}
                            />
                        )}
                    </Grid>

                    {/* next to date picker */}
                    <Grid item xs>
                        <Grid container spacing={2}>

                            {/* court */}
                            <Grid item xs={12} md={6}>
                                <CourtPicker
                                    club={club}
                                    court={court}
                                    error={error}
                                    onChange={setCourt}
                                />
                            </Grid>

                            {/* duration */}
                            <Grid item xs={12} md={6}>
                                <DurationPicker
                                    bookingRules={bookingRules}
                                    club={club}
                                    disabled={isAllDay}
                                    duration={duration}
                                    error={error}
                                    type={type}
                                    onChange={setDuration}
                                />
                            </Grid>

                            {/* repeat */}
                            {typeRules?.repeated && (
                                <>
                                    <Grid item xs={6}>
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                        >
                                            <InputLabel>{i18n.repeat}</InputLabel>
                                            <Select
                                                label={i18n.repeat}
                                                value={repeatType}
                                                onChange={(e) => setRepeatType(e.target.value as typeof repeatType)}
                                            >
                                                {typeRules.availableRepeatTypes.map((t) => (
                                                    <MenuItem key={t} value={t}>{i18n.repeatTypes[t]}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <DatePicker
                                            autoOk
                                            disabled={repeatType === "never"}
                                            disablePast
                                            format="DD.MM.YYYY"
                                            fullWidth
                                            inputVariant="outlined"
                                            label={i18n.repeatUntil}
                                            value={repeatEndDate}
                                            variant="inline"
                                            onChange={(m) => setRepeatEndDate(m!.startOf("day").format("YYYY-MM-DD"))}
                                        />
                                    </Grid>
                                </>
                            )}

                            {/* all day */}
                            {(type === BookingType.Blocking || type === BookingType.Training) && (
                                <Grid item xs={6}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={allDay}
                                                    color="primary"
                                                    onChange={(e) => setAllDay(e.target.checked)}
                                                />
                                            )}
                                            label={i18n.allDay}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {/* title & description */}
                            {typeRules?.requiresDescription && (
                                <>
                                    <GridTextField
                                        error={error === "descriptionTooShort"}
                                        helperText={i18n.titleLength}
                                        label={i18n.title}
                                        maxLength={40}
                                        size={12}
                                        value={description}
                                        onChange={setDescription}
                                    />
                                    <GridTextField
                                        error={detailsDescriptionLength > config.bookings.detailsDescriptionMaxLength}
                                        helperText={detailsDescriptionLength > config.bookings.detailsDescriptionMaxLength - 28 ? `${detailsDescriptionLength} / ${config.bookings.detailsDescriptionMaxLength}` : undefined}
                                        label={i18n.description}
                                        maxLength={config.bookings.detailsDescriptionAbsoluteMaxLength}
                                        multiline
                                        size={12}
                                        value={detailsDescription}
                                        onChange={setDetailsDescription}
                                    />
                                </>
                            )}

                            {/* participants */}
                            {typeRules?.allowsParticipants && (
                                <>
                                    <Grid item xs={12}>
                                        <ParticipantsPicker
                                            coaches={coaches}
                                            court={court}
                                            error={error}
                                            participants={participants}
                                            onChange={(p, c) => { setParticipants(p); setCoaches(c); }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl disabled={club?.capabilities.includes("corona-ready")}>
                                            <FormControlLabel
                                                control={(
                                                    <Checkbox
                                                        checked={coronaTracing || club?.capabilities.includes("corona-ready")}
                                                        color="primary"
                                                        onChange={(e) => setCoronaTracing(e.target.checked)}
                                                    />
                                                )}
                                                label={i18n.coronaTracing}
                                            />
                                        </FormControl>
                                    </Grid>
                                </>
                            )}

                            {/* tags */}
                            {type === BookingType.Training && userProfile?.userTypes?.includes("root-management") && (
                                <>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            filterSelectedOptions
                                            getOptionDisabled={(option) => !(publicTag.some((f) => f.token === option.token))}
                                            getOptionLabel={(option) => (option.token)}
                                            multiple
                                            options={publicTag}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={i18n.tags}
                                                    placeholder={i18n.tagName}
                                                    variant="outlined"
                                                />
                                            )}
                                            renderTags={(value, getTagProps) => value.map((option, index) => (
                                                <Chip
                                                    color="primary"
                                                    style={{ backgroundColor: `${option.tagColor}` }}
                                                    label={`${option.token}`}
                                                    {...getTagProps({ index })}
                                                />
                                            ))}
                                            value={tags}
                                            onChange={(e, v) => setTags(v)}
                                        />
                                    </Grid>
                                </>
                            )}

                            {/* company-exclusive */}
                            {type === BookingType.Training && canCreateCompanyExclusive() && (
                                <Grid item xs={12}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={companyExclusive}
                                                    color="primary"
                                                    onChange={(e) => {
                                                        setCompanyExclusive(e.target.checked);
                                                        if (e.target.checked) {
                                                            setPaywhatyouwant(false);
                                                        }
                                                    }}
                                                />
                                            )}
                                            label={i18n.companyExclusive}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {/* ticked company exclusive / All Organisation */}
                            {companyExclusive && type === BookingType.Training && canCreateCompanyExclusive() && (
                                <>
                                    <Grid item xs={isRootUser() ? 6 : 12}>
                                        <Autocomplete
                                            getOptionSelected={(option, value) => option.clubName === value.clubName}
                                            getOptionLabel={(option) => option.clubName}
                                            options={companyClubs}
                                            value={selectedCompanyExclusiveClub}
                                            onChange={(e, v) => onSelectExclusiveClub(v)}
                                            onInputChange={(event, newInputValue, r) => {
                                                if (companyClubs.length > 0) {
                                                    const clubId = companyClubs.filter((c) => c.clubName === newInputValue);
                                                    if (clubId.length > 0) {
                                                        setExclusiveCompanyClubId(clubId[0].id);
                                                        if (clubId[0].id !== "all") {
                                                            setStandardParticipationPrice("0");
                                                        }
                                                        if (clubId[0].id === "all") {
                                                            setExclusiveCompanyPriceCents("0");
                                                        }
                                                    } else {
                                                        setExclusiveCompanyClubId("all");
                                                        setExclusiveCompanyPriceCents("0");
                                                    }
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={i18n.exclusiveCompanyClubLbl}
                                                    variant="outlined"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    {isRootUser() && (
                                        <Grid item xs={6}>
                                            <CurrencyTextField
                                                type="text"
                                                disabled={exclusiveCompanyClubId === "" || exclusiveCompanyClubId === "all"}
                                                currencySymbol="€"
                                                decimalCharacter=","
                                                digitGroupSeparator="."
                                                fullWidth
                                                label={i18n.exclusiveCompanyPriceCentsLbl}
                                                maximumValue="1000000"
                                                minimumValue="0"
                                                value={exclusiveCompanyPriceCents}
                                                variant="outlined"
                                                onBlur={(e: any, value: number) => {
                                                    setExclusiveCompanyPriceCents(value.toString());
                                                }}
                                            />
                                        </Grid>
                                    )}
                                </>
                            )}

                            {/* blocked for */}
                            {typeRules?.blueprints && (
                                <Grid item xs={6}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>{i18n.blockedFor}</InputLabel>
                                        <Select
                                            label={i18n.blockedFor}
                                            value={blockingBlueprint}
                                            onChange={(e) => setBlockingBlueprint(e.target.value as typeof blockingBlueprint)}
                                        >
                                            {typeRules.blueprints.map((t) => (
                                                <MenuItem key={t} value={t}>{i18n.blockingBlueprints[t]}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* audience group */}
                            {(typeRules?.blueprints && blockingBlueprint === "demographic-blocking") && (
                                <Grid item xs={12}>
                                    <AudienceSelect
                                        defaultValue={audienceGroupSpecification}
                                        onChange={setAudienceGroupSpecification}
                                    />
                                    <FormControl>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={addAudienceGroupException}
                                                    color="primary"
                                                    onChange={(e) => setAddAudienceGroupException(e.target.checked)}
                                                />
                                            )}
                                            label={i18n.addException}
                                        />
                                    </FormControl>
                                    {addAudienceGroupException && (
                                        <AudienceSelect
                                            defaultValue={negatedAudienceGroupSpecification}
                                            onChange={setNegatedAudienceGroupSpecification}
                                        />
                                    )}
                                </Grid>
                            )}

                            {/* advanced settings */}
                            {!showAdvanced && (
                                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                                    <Button onClick={() => setShowAdvanced(true)}>
                                        {i18n.advanced}
                                    </Button>
                                </Grid>
                            )}

                            {/* participants must check-in individually */}
                            {showAdvanced && (
                                <GridTextField
                                    label={i18n.privateDescription}
                                    maxLength={255}
                                    multiline
                                    size={12}
                                    value={privateDescription}
                                    onChange={setPrivateDescription}
                                />
                            )}

                            {/* participants must check-in individually */}
                            {showAdvanced && typeRules?.allowsParticipantSpecificCheckIn && (
                                <Grid item xs={12}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={participantSpecificCheckIn}
                                                    color="primary"
                                                    onChange={(e) => setParticipantSpecificCheckIn(e.target.checked)}
                                                />
                                            )}
                                            label={i18n.participantSpecificCheckIn}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {/* min/max participants, participation requests */}
                            {showAdvanced && typeRules?.allowsParticipants && (
                                <>
                                    <Grid item xs={6}>
                                        <NumberPicker
                                            label={i18n.minNumParticipants}
                                            min={(type === BookingType.Standard) ? 2 : 0}
                                            value={customMinNumParticipants}
                                            onChange={setCustomMinNumParticipants}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <NumberPicker
                                            label={i18n.maxNumParticipants}
                                            min={0}
                                            value={customMaxNumParticipants}
                                            zeroPlaceholder="&infin;" // infinity symbol
                                            onChange={setCustomMaxNumParticipants}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel>{i18n.participationRequests}</InputLabel>
                                            <Select
                                                label={i18n.participationRequests}
                                                value={participationRequestType}
                                                onChange={(e) => setParticipationRequestType(e.target.value as typeof participationRequestType)}
                                            >
                                                {["requestable-by-followers", "invite-only", "auto-accept"].map((t) => (
                                                    <MenuItem key={t} value={t}>{i18n.participationRequestType[t]}</MenuItem>
                                                ))}
                                                {(type === BookingType.Training) && (
                                                    <MenuItem value="auto-accept-for-group">{i18n.participationRequestType["auto-accept-for-group"]}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {(participationRequestType === "auto-accept-for-group") && (
                                        <Grid item xs={12}>
                                            <AudienceSelect
                                                defaultValue={autoAcceptParticipationForGroup}
                                                onChange={setAutoAcceptParticipationForGroup}
                                            />
                                        </Grid>
                                    )}
                                </>
                            )}

                            {/* finalization deadline */}
                            {showAdvanced && typeRules?.allowsFinalizationDeadline && (
                                <Grid item xs={6}>
                                    <DateSelect
                                        disablePast
                                        helperText={(repeatType !== "never" && finalizationDeadline) ? i18n.finalizationDeadlineRepeatedWarn : ""}
                                        initialFocusedDate={startDate}
                                        label={i18n.finalizationDeadline}
                                        value={finalizationDeadline}
                                        variant="outlined"
                                        withTime
                                        onChange={(m) => setFinalizationDeadline(m?.set({ seconds: 0, milliseconds: 0 }).toISOString())}
                                    />
                                </Grid>
                            )}

                            {/* pay what you want */}
                            {showAdvanced && !companyExclusive && typeRules?.allowsStandardParticipationPrice && (
                                <Grid item xs={12}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={paywhatyouwant}
                                                    color="primary"
                                                    onChange={(e) => {
                                                        setStandardParticipationPrice("0");
                                                        setPaywhatyouwant(e.target.checked);
                                                        fetchFundsRecipients();
                                                    }}
                                                />
                                            )}
                                            label={i18n.paywhatyouwant}
                                            disabled={Number(standardParticipationPrice) > 0}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {/* participation fee, funds recipient */}
                            {showAdvanced && !companyExclusive && typeRules?.allowsStandardParticipationPrice && (
                                <>
                                    <Grid item xs={6}>
                                        <CurrencyTextField
                                            type="text"
                                            disabled={paywhatyouwant || (companyExclusive && exclusiveCompanyClubId !== "all" && exclusiveCompanyClubId !== "")}
                                            currencySymbol="€"
                                            decimalCharacter=","
                                            digitGroupSeparator="."
                                            fullWidth
                                            label={i18n.standardParticipationPriceCents}
                                            helperText={Number(standardParticipationPrice) > 50 ? i18n.standardParticipationPriceCentsWarn : ""}
                                            maximumValue={100}
                                            minimumValue={0}
                                            value={standardParticipationPrice}
                                            variant="outlined"
                                            onBlur={(e: any, value: number) => {
                                                if (value) {
                                                    setStandardParticipationPrice(value.toString());
                                                } else {
                                                    setStandardParticipationPrice("0,00");
                                                }
                                            }}
                                        />
                                    </Grid>
                                    {(Number(standardParticipationPrice) > 0 || paywhatyouwant) && (
                                        <Grid item xs={6}>
                                            <FormControl
                                                fullWidth
                                                variant="outlined"
                                            >
                                                <InputLabel>{i18n.fundsRecipient}</InputLabel>
                                                <Select
                                                    label={i18n.fundsRecipient}
                                                    value={fundsRecipientId}
                                                    onChange={(e) => setFundsRecipientId(e.target.value as string)}
                                                >
                                                    {fundsRecipientChoices.map((c) => (
                                                        <MenuItem key={c.id} value={c.id}>{c.displayName}</MenuItem>
                                                    ))}
                                                </Select>
                                                {fundsRecipientChoices.length > 0 && fundsRecipientChoices[0].id !== fundsRecipientId && (
                                                    <FormHelperText>{i18n.fundsRecipientWarn}</FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    )}
                                </>
                            )}

                            {/* hide creator */}
                            {showAdvanced && (type === BookingType.Training || type === BookingType.Blocking) && (
                                <Grid item xs={12}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={hideCreator}
                                                    color="primary"
                                                    onChange={(e) => setHideCreator(e.target.checked)}
                                                />
                                            )}
                                            label={i18n.hideCreator}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {/* event type */}
                            {showAdvanced && type === BookingType.Training && (
                                <Grid item xs={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                    >
                                        <InputLabel>{i18n.eventType}</InputLabel>
                                        <Select
                                            label={i18n.eventType}
                                            value={eventType}
                                            onChange={(e) => setEventType(e.target.value as typeof eventType)}
                                        >
                                            {["standard", "competition", "video-stream", "video-conference"].map((t) => (
                                                <MenuItem key={t} value={t}>{i18n.eventTypes[t]}</MenuItem>
                                            ))}
                                            {club?.courts.find((c) => c.id === court)?.isCommercialOnlyCourt && (
                                                <MenuItem value="commercial">{i18n.eventTypes.commercial}</MenuItem>
                                            )}
                                        </Select>
                                        {eventType === "video-conference" && (
                                            <FormHelperText>{i18n.videoConferenceParticipantsNotice}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            )}

                            {/* sport form */}
                            {showAdvanced && type === BookingType.Training && (
                                <Grid item xs={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                    >
                                        <InputLabel>{i18n.sportForm}</InputLabel>
                                        <Select
                                            label={i18n.sportForm}
                                            value={sportForm}
                                            onChange={(e) => setSportForm(e.target.value as string)}
                                        >
                                            {courtObject?.sportForms.map((t) => (
                                                <MenuItem key={t} value={t}>{uI18n.sportForms[t]}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* insert media */}
                            {showAdvanced && type === BookingType.Training && (
                                <>
                                    <Grid item xs={12}>
                                        {/* creator media only */}
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={creatorMediaOnly}
                                                    color="primary"
                                                    onChange={(e) => setCreatorMediaOnly(e.target.checked)}
                                                />
                                            )}
                                            label={i18n.creatorMediaOnly}
                                        />
                                        {/* insert media input */}
                                        <input
                                            accept="image/*,video/*"
                                            ref={insertMediaRef}
                                            style={{ display: "none" }}
                                            type="file"
                                            onChange={(e) => { if (e.target.files![0]) setAttachedMedia([...attachedMedia, e.target.files![0]]); }}
                                        />

                                        {/* buttons */}
                                        <div style={{ display: "flex", flexWrap: "wrap" }}>
                                            {attachedMedia.map((f, i) => (
                                                // eslint-disable-next-line
                                                <Chip key={`${f.name}_${i}`} label={f.name} style={{ margin: 4 }} onDelete={() => setAttachedMedia(attachedMedia.filter((_, j) => j !== i))} />
                                            ))}
                                        </div>
                                        {attachedMedia.length < 5 && (
                                            <Button onClick={() => insertMediaRef.current!.click()}>{i18n.addMedia}</Button>
                                        )}
                                    </Grid>
                                    {attachedMedia.length > 0 && (
                                        <GridTextField
                                            label={i18n.mediaComment}
                                            maxLength={288}
                                            multiline
                                            size={12}
                                            value={attachedMediaComment}
                                            onChange={setAttachedMediaComment}
                                        />
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>

                    {/* unknown error */}
                    {!relevantErrors.includes(error!) && (
                        <Grid item xs={12}>
                            <FormHelperText error>{error}</FormHelperText>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" disabled={isLoading} onClick={createBooking}>
                    {i18n.book(bookingRules.priceCents ? formatter.format(bookingRules.priceCents / 100) : "")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateBookingDialog;
